import { cn } from "./lib/cn.js";

/**
 * Skeleton primitive.
 *
 * Canon: docs/design-canon/01-ui/feedback-states.md R2.
 *
 * A neutral pulsing placeholder shaped like the content it stands
 * in for. Three convenience variants (`<SkeletonLine>`,
 * `<SkeletonBlock>`, `<SkeletonCard>`) cover the common cases; the
 * raw `<Skeleton>` is for one-off shapes.
 *
 * Use this instead of a spinner for initial page loads — the user
 * perceives the layout taking shape rather than a wait.
 */

export interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
  return <div aria-hidden="true" className={cn("animate-pulse rounded bg-border", className)} />;
}

/**
 * One row of fake text. Default width 100%; override via className.
 */
export function SkeletonLine({ className }: SkeletonProps) {
  return <Skeleton className={cn("h-3 w-full", className)} />;
}

/**
 * A solid rectangular placeholder for a card / image / chart.
 */
export function SkeletonBlock({ className }: SkeletonProps) {
  return <Skeleton className={cn("h-24 w-full", className)} />;
}

/**
 * A common card-shaped skeleton: a heading line, two body lines, and
 * a footer line. Used for grid / list-card placeholders.
 */
export function SkeletonCard({ className }: SkeletonProps) {
  return (
    <div className={cn("space-y-3 rounded-soft-lg border bg-surface p-4 shadow-sm", className)}>
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-5/6" />
      <Skeleton className="h-3 w-1/3" />
    </div>
  );
}
